<template>
	<div class="branches-page">
		<!-- definePageMeta -->
		<Head>
			<Title>{{
				metaContent?.title ? ensureBrandSuffix(metaContent.title) : `${locale === 'pl' ? cmsLocation?.namePL : cmsLocation?.nameEN} | CarFree`
			}}</Title>
			<Meta name="description" :content="metaContent?.description ?? ''" />
			<Meta v-if="config.public.isIndex" name="robots" :content="robotsCalculated" />
			<Meta v-else name="robots" content="noindex, nofollow" />
			<Link
				rel="canonical"
				:href="metaContent?.canonical && metaContent?.canonical?.trim() !== '' ? metaContent?.canonical : `${url.origin}${url.pathname}`" />

			<!-- OG tags -->
			<Meta
				property="og:title"
				:content="
					metaContent?.title
						? ensureBrandSuffix(metaContent.title)
						: `${locale === 'pl' ? cmsLocation?.namePL : cmsLocation?.nameEN} | CarFree`
				" />
			<Meta property="og:description" :content="metaContent?.description ?? ''" />
			<Meta
				property="og:image"
				:content="structuralDataIMG ? `${storageUrl}${structuralDataIMG}` : bannerIMG ? `${storageUrl}${bannerIMG}` : ''" />
			<Meta
				property="og:url"
				:content="
					metaContent?.canonical && metaContent?.canonical?.trim() !== '' ? metaContent?.canonical : `${url.origin}${url.pathname}`
				" />
			<Meta property="og:type" content="website" />
			<Meta property="og:site_name" content="CarFree" />

			<Meta name="twitter:card" content="summary_large_image" />
			<Meta
				name="twitter:title"
				:content="metaContent?.title ? metaContent?.title : `${locale === 'pl' ? cmsLocation?.namePL : cmsLocation?.nameEN} | CarFree`" />
			<Meta name="twitter:description" :content="metaContent?.description ?? ''" />
			<Meta
				name="twitter:image"
				:content="structuralDataIMG ? `${storageUrl}${structuralDataIMG}` : bannerIMG ? `${storageUrl}${bannerIMG}` : ''" />
		</Head>

		<GlobalHeader />
		<CarRentalPageHero v-if="isElectricCarsPage" :title="$t('pages.electricCars.header')" />
		<LocationPageHero v-else />
		<LocationBreadcrumbs />
		<template v-if="isElectricCarsPage">
			<LazyLongTermPageBenefits />
			<LazyLongTermPageContact :min-val="1" :max-val="30" :label="EFormLabel.DAYS" :timeframe="EContactFormRentalTimeframe.SHORT_TERM" />
		</template>
		<template v-else>
			<LazyLocationMapSection />
			<LazyLocationTestimonials />
		</template>
		<div class="branches-page__cars-carousell">
			<HomePageChooseCar
				v-if="cmsLocation"
				:gray-bg="true"
				noTitle
				whiteCard
				:oneGroup="isElectricCarsPage ? EGroupName.ELECTRIC : carGroupSwitch"
				init />
		</div>
		<LazyLocationTextSection />
		<LazyLocationFaq />
		<LazyLocationClosestBranches v-if="!isElectricCarsPage" />
		<LazyLocationTextSection bottom />
		<LazyGlobalApplicationBanner :without-margin="true" />
	</div>
</template>

<script setup lang="ts">
import { ELocationSectionType } from '@/modules/adminModule/constants/pagesCMSconstants'
const url = useRequestURL()
const { locale } = useI18n()
const { $notyf } = useNuxtApp()
const route = useRoute()
const locationRedirectStore = useLocationRedirectStore()
const i18nService = usei18nService()
const bookingStore = useBookingStore()
const reservationStore = useReservationStore()
// sections
const pageSections = ref<ICMSLocationContent[]>()
const pageHeaderSection = ref<ICMSLocationContent>()
const pageMapSection = ref<ICMSLocationContent>()
const pageTestimonialsSection = ref<ICMSLocationContent>()
const pageTextSection = ref<ICMSLocationContent>()
const pageFaqSection = ref<ICMSLocationContent>()
const pageTextSection2 = ref<ICMSLocationContent>()
const pageMetaObject = ref<ICMSLocationContent>()
const metaContent = ref<ICMSLocationContentTranslations>()
const structuralDataSection = ref<ICMSLocationContentTranslations>()
const config = useNuxtApp().$config
const cmsLocation = ref<ICMSLocationModel>()
const bookerTarget = ref<HTMLElement | null>(null)
const assignedLocations = ref<ILocationModel[]>([])
const closestLocations = ref<ICMSLocationModel[]>([])
const sameCityLocations = ref<ICMSLocationModel[]>([])
const router = useRouter()
const localePath = useLocalePath()
const rentalLocationPagesStore = useRentalLocationPagesStore()
const carGroupSwitch = computed(() => {
	const cmsLocationLink = cmsLocation.value?.urlPL
	let group: EGroupName | undefined
	if (!cmsLocationLink) return undefined
	// const linkLocation = route.path

	// if (linkLocation.includes('elektrycznych')) {
	// 	return EGroupName.ELECTRIC
	// } else if (linkLocation.includes('busow-osobowych') || linkLocation.includes('-bus')) {
	// 	return EGroupName.VAN
	// } else return undefined

	if (cmsLocationLink.includes('elektrycznych') || cmsLocationLink.includes('tesla')) {
		group = EGroupName.ELECTRIC
	} else if (cmsLocationLink.includes('dostawczy')) {
		group = EGroupName.DELIVERY
	} else if (cmsLocationLink.includes('bus')) {
		group = EGroupName.VAN
	}

	return group
})

const { storageUrl } = useStorageService()

const isElectricCarsPage = computed(() => {
	return cmsLocation.value?.urlPL?.includes('elektrycznych') || cmsLocation.value?.urlEN?.includes('electric')
})

const fetchCMSLocationByURL = async () => {
	const locationURLSlug = route.params.urlSlug

	try {
		// const { data } = await ApiService.fetchCMSLocationBySlug(locationURLSlug, locale.value, true)
		// if (data) cmsLocation.value = data
		const data = await rentalLocationPagesStore.fetchCmsLocationBySlug(locationURLSlug as string, locale.value)
		if (data) cmsLocation.value = data
	} catch (error) {
		if ((error as IError).message?.includes('Location not found') || (error as IError).message?.includes('Lokalizacja nie znaleziona')) {
			router.push(localePath('/'))
		}
		logger.log(error)
	}
}

const fetchAssignedLocations = async () => {
	if (!cmsLocation.value) return
	try {
		// const { data } = await ApiService.fetchAssignedLocations(cmsLocation.value.id)

		const data = await rentalLocationPagesStore.fetchAssignedLocations(cmsLocation.value.id)

		if (data && data.length > 0) {
			assignedLocations.value = data.map((location) => location.location)
		} else {
			assignedLocations.value = []
		}
	} catch (error) {
		logger.log(error)
	}
}
const fetchSameCityLocations = async () => {
	try {
		if (!cmsLocation.value?.mainCmsLocationId) return
		// server-side filter by main location - pulling the full list here put ~500 kB into every location page payload
		sameCityLocations.value = (await rentalLocationPagesStore.fetchSameCityLocations(cmsLocation.value.mainCmsLocationId)) ?? []
	} catch (error) {
		logger.log(error)
	}
}

const fetchClosestLocations = async () => {
	try {
		if (!cmsLocation.value) return
		// a page with no mainCmsLocationId has an empty sameCityLocations, so excludeIds is just its own id
		const excludeIds = Array.from(new Set([cmsLocation.value.id, ...sameCityLocations.value.map((location) => location.id)]))
		const data = await rentalLocationPagesStore.fetchClosestLocations(cmsLocation.value.id, excludeIds)

		if (data && data.length > 0) {
			closestLocations.value = data
		} else {
			closestLocations.value = []
		}
	} catch (error) {
		logger.log(error)
	}
}

const getPageSections = async () => {
	if (!cmsLocation.value) return
	try {
		// const response = await ApiService.fetchCMSLocationContentSections(cmsLocation.value?.id, locale.value)
		const response = await rentalLocationPagesStore.fetchCMSLocationContentSections(cmsLocation.value?.id, locale.value)
		pageSections.value = response

		const sectionByName = new Map<string, ICMSLocationContent>()
		for (const section of response ?? []) {
			if (section?.name && !sectionByName.has(section.name)) sectionByName.set(section.name, section)
		}

		pageHeaderSection.value = sectionByName.get(ELocationSectionType.PAGE_HEADER_IMAGE)
		pageMapSection.value = sectionByName.get(ELocationSectionType.MAP_SECTION)
		pageTestimonialsSection.value = sectionByName.get(ELocationSectionType.TESTIMONIALS)
		pageTextSection.value = sectionByName.get(ELocationSectionType.CONTENT_SECTION_1)
		pageFaqSection.value = sectionByName.get(ELocationSectionType.FAQ_SECTION)
		pageTextSection2.value = sectionByName.get(ELocationSectionType.CONTENT_SECTION_2)
		pageMetaObject.value = sectionByName.get(ELocationSectionType.META)
		metaContent.value = pageMetaObject.value?.translations.find((translation) => translation.language === locale.value)?.content[0]
		structuralDataSection.value = sectionByName.get(ELocationSectionType.STRUCTURAL_DATA)
	} catch (error) {
		$notyf.error('Error while fetching page sections')
		logger.log(error)
	}
}

await fetchCMSLocationByURL()

await Promise.all([fetchAssignedLocations(), fetchSameCityLocations(), getPageSections()])

// fetchClosestLocations needs sameCityLocations already fetched to build exclude_ids
await fetchClosestLocations()

const bannerIMG = ref<string>()

const structuralDataIMG = ref<string>()

const assignBannerIMG = () => {
	if (!pageHeaderSection.value) return null
	if (pageHeaderSection.value.files && pageHeaderSection.value.files.length > 0) {
		const img = pageHeaderSection.value.files[0].path
		if (img) bannerIMG.value = img
	}
}

assignBannerIMG()

const assignStructuralDataIMG = () => {
	if (!structuralDataSection.value) return null
	if (structuralDataSection.value.files && structuralDataSection.value.files.length > 0) {
		const img = structuralDataSection.value.files[0].path
		if (img) structuralDataIMG.value = img
	}
}

assignStructuralDataIMG()

const robotsCalculated = useRobotsMeta(() => metaContent.value?.robots)

watch(
	() => locationRedirectStore.getRedirectLang,
	(lang) => {
		if (!lang) return
		i18nService.changeLang(lang, cmsLocation.value[`url${lang.toUpperCase()}`])
	},
)

onMounted(() => {
	if (assignedLocations.value.length > 0) {
		bookingStore.setBookerLocations({ start: assignedLocations.value[0], end: null })
		reservationStore.setBookerLocations({ start: assignedLocations.value[0], end: null })
		reservationStore.addLocationToHistory(assignedLocations.value[0].id)
	} else {
		bookingStore.setBookerLocations({ start: null, end: null })
		reservationStore.setBookerLocations({ start: null, end: null })
	}
})

const formatPhoneNumber = (phoneNumber: string) => {
	if (!phoneNumber || phoneNumber?.length < 9) return phoneNumber

	const phone = phoneNumber.replace('+', '')
	return `+${phone.slice(0, 2)} ${phone.slice(2, 5)} ${phone.slice(5, 8)} ${phone.slice(8, 11)}`.trim()
}

const phoneNumber = computed(() => {
	if (cmsLocation.value?.phoneNumber && cmsLocation.value?.phoneNumber !== '') {
		return formatPhoneNumber(cmsLocation.value?.phoneNumber)
	} else if (assignedLocations.value && assignedLocations.value?.length > 0) {
		return formatPhoneNumber(assignedLocations.value[0].phone_number)
	}
	return null
})

// STRUCTURAL DATA
const resolveLocalizedField = (
	field: 'name' | 'description' | 'telephone',
	localeContent?: Record<string, string>,
	fallbackContent?: Record<string, string>,
) => {
	const localeValue = localeContent?.[field]
	if (localeValue && localeValue.trim() !== '' && localeValue !== 'null') return localeValue

	const fallbackValue = fallbackContent?.[field]
	if (fallbackValue && fallbackValue.trim() !== '' && fallbackValue !== 'null') return fallbackValue

	return undefined
}

useAutoRentalJsonld(() => {
	const structuralDataSectionContent = structuralDataSection.value?.translations.find((translation) => translation.language === locale.value)
		?.content[0]
	const structuralDataSectionContentPL = structuralDataSection.value?.translations.find((translation) => translation.language === 'pl')?.content[0]

	const name =
		resolveLocalizedField('name', structuralDataSectionContent, structuralDataSectionContentPL) ??
		(locale.value === 'pl' ? cmsLocation.value?.namePL : cmsLocation.value?.nameEN)
	const description = resolveLocalizedField('description', structuralDataSectionContent, structuralDataSectionContentPL) ?? ''
	const telephone =
		resolveLocalizedField('telephone', structuralDataSectionContent, structuralDataSectionContentPL) ?? phoneNumber.value ?? undefined

	const geoPoint = cmsLocation.value?.geoPoint
	const geo = geoPoint ? { latitude: geoPoint.coordinates[1], longitude: geoPoint.coordinates[0] } : undefined

	return {
		url: `${url.origin}${url.pathname}`,
		name,
		description,
		geo,
		content: {
			telephone,
			isAddress: structuralDataSectionContentPL?.isAddress,
			isPickupPoint: structuralDataSectionContentPL?.isPickupPoint,
			streetAddress: structuralDataSectionContent?.streetAddress ?? structuralDataSectionContentPL?.streetAddress,
			addressLocality: structuralDataSectionContent?.addressLocality ?? structuralDataSectionContentPL?.addressLocality,
			addressRegion: structuralDataSectionContent?.addressRegion ?? structuralDataSectionContentPL?.addressRegion,
			postalCode: structuralDataSectionContent?.postalCode ?? structuralDataSectionContentPL?.postalCode,
		},
	}
})

definePageMeta({
	middleware: ['car-rental'],
})

usePageViewed(EGTMPageType.LOCATION_LANDING)

provide('bookerTarget', bookerTarget)
provide('cmsLocation', cmsLocation)
provide('assignedLocations', assignedLocations)
provide('closestLocations', closestLocations)
provide('sameCityLocations', sameCityLocations)
provide('pageSections', pageSections)
provide('pageHeaderSection', pageHeaderSection)
provide('pageMapSection', pageMapSection)
provide('pageTestimonialsSection', pageTestimonialsSection)
provide('pageTextSection', pageTextSection)
provide('pageFaqSection', pageFaqSection)
provide('pageTextSection2', pageTextSection2)
</script>

<style scoped lang="scss" src="./index.scss"></style>
